<template>
  <div class="sb-page">
    <header class="sb-header">
      <div class="sb-header__inner">
        <div class="sb-header__text">
          <p class="sb-brand">印章库存看板</p>
          <h1 class="sb-title">{{ title || '库存一览' }}</h1>
          <p class="sb-sub">无需登录 · 实时库存 · 共 {{ list.length }} 种物料 · {{ isDesktop ? '列表视图' : '卡片视图' }}</p>
        </div>
        <button type="button" class="sb-refresh" :disabled="loading" @click="loadList">
          {{ loading ? '刷新中…' : '刷新' }}
        </button>
      </div>
    </header>

    <main class="sb-main">
      <div v-if="errorMsg" class="sb-error">{{ errorMsg }}</div>
      <div v-else-if="loading && !list.length" class="sb-loading">加载中…</div>
      <template v-else>
        <div v-if="!list.length" class="sb-empty">暂无库存物料</div>
        <template v-else>
          <!-- 移动端：卡片 -->
          <section class="sb-cards" aria-label="库存卡片">
            <article
              v-for="row in list"
              :key="'c-' + row.id"
              class="sb-card"
              :class="{ 'is-empty': (row.stockQty || 0) <= 0 }"
            >
              <div class="sb-card__imgs">
                <div class="sb-img-box">
                  <img v-if="imgUrl(row.ruleImg)" :src="imgUrl(row.ruleImg)" alt="底部图" />
                  <span v-else class="sb-img-ph">底部</span>
                  <em>底部</em>
                </div>
                <div class="sb-img-box">
                  <img v-if="imgUrl(row.handleImage)" :src="imgUrl(row.handleImage)" alt="手柄图" />
                  <span v-else class="sb-img-ph">手柄</span>
                  <em>手柄</em>
                </div>
              </div>
              <div class="sb-card__body">
                <div class="sb-name">{{ row.ruleName || row.skuName || '印章' }}</div>
                <div class="sb-meta">手柄：{{ row.handleName || '—' }}</div>
                <div class="sb-code">{{ row.skuCode || '—' }}</div>
                <div class="sb-qty" :data-empty="(row.stockQty || 0) <= 0">
                  库存 <strong>{{ row.stockQty != null ? row.stockQty : 0 }}</strong>
                  <span v-if="row.unit"> {{ row.unit }}</span>
                </div>
              </div>
            </article>
          </section>

          <!-- PC：列表 -->
          <section class="sb-table-wrap" aria-label="库存列表">
            <table class="sb-table">
              <thead>
                <tr>
                  <th class="col-img">底部图</th>
                  <th class="col-name">印章底部</th>
                  <th class="col-img">手柄图</th>
                  <th class="col-handle">手柄</th>
                  <th class="col-code">物料编码</th>
                  <th class="col-qty">库存</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in list"
                  :key="'t-' + row.id"
                  :class="{ 'is-empty': (row.stockQty || 0) <= 0 }"
                >
                  <td class="col-img">
                    <img v-if="imgUrl(row.ruleImg)" class="sb-thumb" :src="imgUrl(row.ruleImg)" alt="" />
                    <span v-else class="sb-thumb-ph">—</span>
                  </td>
                  <td class="col-name">{{ row.ruleName || row.skuName || '—' }}</td>
                  <td class="col-img">
                    <img v-if="imgUrl(row.handleImage)" class="sb-thumb" :src="imgUrl(row.handleImage)" alt="" />
                    <span v-else class="sb-thumb-ph">—</span>
                  </td>
                  <td class="col-handle">{{ row.handleName || '—' }}</td>
                  <td class="col-code">{{ row.skuCode || '—' }}</td>
                  <td class="col-qty">
                    <span class="sb-qty-num" :data-empty="(row.stockQty || 0) <= 0">
                      {{ row.stockQty != null ? row.stockQty : 0 }}
                    </span>
                    <span v-if="row.unit" class="sb-unit">{{ row.unit }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </template>
      </template>
    </main>
  </div>
</template>

<script>
import { fetchPublicSealStockBoard } from '@/api/seal-stock-public'

const DESKTOP_MQ = '(min-width: 768px)'

export default {
  name: 'SealStockBoardPublic',
  data() {
    return {
      token: '',
      loading: true,
      errorMsg: '',
      title: '',
      list: [],
      isDesktop: false
    }
  },
  created() {
    this.syncViewport()
    if (typeof window !== 'undefined' && window.matchMedia) {
      this._mq = window.matchMedia(DESKTOP_MQ)
      this._onMq = () => {
        this.isDesktop = !!this._mq.matches
      }
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._onMq)
      } else if (this._mq.addListener) {
        this._mq.addListener(this._onMq)
      }
    }
    this.token = String((this.$route.query && this.$route.query.token) || '').trim()
    if (!this.token) {
      this.loading = false
      this.errorMsg = '链接无效：缺少访问参数 token'
      return
    }
    this.loadList()
  },
  beforeDestroy() {
    if (this._mq && this._onMq) {
      if (this._mq.removeEventListener) {
        this._mq.removeEventListener('change', this._onMq)
      } else if (this._mq.removeListener) {
        this._mq.removeListener(this._onMq)
      }
    }
  },
  methods: {
    syncViewport() {
      if (typeof window !== 'undefined' && window.matchMedia) {
        this.isDesktop = window.matchMedia(DESKTOP_MQ).matches
      }
    },
    imgUrl(url) {
      if (!url) return ''
      const s = String(url).trim()
      if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('data:')) return s
      if (s.indexOf('/upload/') === 0 || s.indexOf('upload/') === 0) {
        return `https://wvxvb.cn/upload/${s.replace(/^\/?upload\/?/, '')}`
      }
      return s
    },
    loadList() {
      this.loading = true
      this.errorMsg = ''
      fetchPublicSealStockBoard(this.token)
        .then((data) => {
          this.title = (data && data.title) || '印章库存看板'
          this.list = (data && data.list) || []
        })
        .catch((e) => {
          this.errorMsg = (e && e.message) || '加载失败'
          this.list = []
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.sb-page {
  --bg: #eef2f0;
  --ink: #1a2228;
  --muted: #667079;
  --card: #fffcf7;
  --line: #d9e0dc;
  --accent: #0b6b52;
  --danger: #c0352b;
  min-height: 100vh;
  background:
    linear-gradient(180deg, #d7e8e1 0%, transparent 220px),
    var(--bg);
  color: var(--ink);
  font-family: "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
}

.sb-header {
  padding: 24px 16px 8px;
}

.sb-header__inner {
  max-width: 1100px;
  margin: 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.sb-brand {
  margin: 0;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 700;
}

.sb-title {
  margin: 8px 0 4px;
  font-size: clamp(22px, 5vw, 34px);
  font-weight: 760;
  line-height: 1.15;
}

.sb-sub {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}

.sb-refresh {
  appearance: none;
  border: 1px solid var(--line);
  background: #fff;
  color: var(--ink);
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  flex-shrink: 0;
}

.sb-refresh:disabled {
  opacity: 0.6;
}

.sb-main {
  max-width: 1100px;
  margin: 0 auto;
  padding: 12px 16px 40px;
}

.sb-empty,
.sb-loading,
.sb-error {
  padding: 20px;
  border-radius: 14px;
  border: 1px dashed var(--line);
  background: rgba(255, 252, 247, 0.8);
  text-align: center;
  color: var(--muted);
}

.sb-error {
  border-style: solid;
  color: var(--danger);
  background: #fff5f4;
}

/* ===== 移动端卡片（默认显示） ===== */
.sb-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.sb-table-wrap {
  display: none;
}

.sb-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 6px 18px rgba(26, 34, 40, 0.04);
}

.sb-card.is-empty {
  opacity: 0.78;
}

.sb-card__imgs {
  display: flex;
  gap: 8px;
}

.sb-img-box {
  width: 72px;
  text-align: center;
}

.sb-img-box img,
.sb-img-ph {
  width: 72px;
  height: 72px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: #f1ece4;
  object-fit: cover;
  display: block;
}

.sb-img-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0a8ae;
  font-size: 12px;
}

.sb-img-box em {
  display: block;
  margin-top: 4px;
  font-style: normal;
  font-size: 11px;
  color: var(--muted);
}

.sb-name {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.sb-meta,
.sb-code {
  margin-top: 4px;
  font-size: 13px;
  color: var(--muted);
}

.sb-qty {
  margin-top: 10px;
  font-size: 14px;
}

.sb-qty strong {
  font-size: 28px;
  font-weight: 780;
  color: var(--accent);
  margin: 0 2px;
}

.sb-qty[data-empty='true'] strong {
  color: var(--danger);
}

/* ===== PC 列表（≥768px） ===== */
@media (min-width: 768px) {
  .sb-cards {
    display: none;
  }

  .sb-table-wrap {
    display: block;
    background: var(--card);
    border: 1px solid var(--line);
    border-radius: 16px;
    overflow: auto;
    box-shadow: 0 8px 24px rgba(26, 34, 40, 0.05);
  }

  .sb-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;
  }

  .sb-table th,
  .sb-table td {
    padding: 12px 14px;
    border-bottom: 1px solid var(--line);
    text-align: left;
    vertical-align: middle;
    font-size: 14px;
  }

  .sb-table th {
    background: #f4f7f5;
    color: var(--muted);
    font-weight: 650;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .sb-table tbody tr:hover {
    background: #f8faf9;
  }

  .sb-table tbody tr.is-empty {
    opacity: 0.72;
  }

  .sb-table tbody tr:last-child td {
    border-bottom: 0;
  }

  .col-img {
    width: 88px;
    text-align: center;
  }

  .col-qty {
    width: 120px;
    text-align: right;
    white-space: nowrap;
  }

  .col-code {
    color: var(--muted);
    font-variant-numeric: tabular-nums;
  }

  .sb-thumb {
    width: 52px;
    height: 52px;
    border-radius: 10px;
    border: 1px solid var(--line);
    object-fit: cover;
    background: #f1ece4;
    vertical-align: middle;
  }

  .sb-thumb-ph {
    display: inline-flex;
    width: 52px;
    height: 52px;
    border-radius: 10px;
    border: 1px dashed var(--line);
    align-items: center;
    justify-content: center;
    color: #a0a8ae;
    font-size: 12px;
    background: #f7f3ec;
  }

  .sb-qty-num {
    font-size: 22px;
    font-weight: 780;
    color: var(--accent);
    font-variant-numeric: tabular-nums;
  }

  .sb-qty-num[data-empty='true'] {
    color: var(--danger);
  }

  .sb-unit {
    margin-left: 4px;
    color: var(--muted);
    font-size: 12px;
  }
}
</style>
